import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/*
 * Cool Mist (light) or Midnight Navy (dark).
 *
 * Light is the default: the portal is used all day under salon lighting, and an
 * operator who never touches the toggle should get the theme the foundation was
 * measured against. Dark is opt-in and remembered.
 *
 * The class lands on <html> so the `.dark` variable block in index.scss wins;
 * the inline script in public/index.html applies it before first paint so a
 * reload never flashes the wrong theme.
 */

const STORAGE_KEY = "puralance.business.theme";

const ThemeContext = createContext({ theme: "light", toggleTheme: () => {} });

const stored = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "dark" || value === "light" ? value : null;
  } catch {
    // Private mode, or storage blocked. Fall through to the default.
    return null;
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => stored() ?? "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Not being able to remember the choice is not a reason to drop it.
    }
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((current) => (current === "dark" ? "light" : "dark")),
    []
  );

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
